import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { newsletterService } from '../../../services/newsletterService';
import { blogService } from '../../../services/blogService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Default social links with Facebook included and correct icons
  const [socialLinks, setSocialLinks] = useState([
    { name: 'Twitter', icon: 'Twitter', url: 'https://twitter.com', isActive: true },
    { name: 'Facebook', icon: 'Facebook', url: 'https://facebook.com', isActive: true },
    { name: 'LinkedIn', icon: 'LinkedIn', url: 'https://linkedin.com', isActive: true },
    { name: 'YouTube', icon: 'Youtube', url: 'https://youtube.com', isActive: true },
    { name: 'Discord', icon: 'MessageCircle', url: 'https://discord.com', isActive: true }
  ]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Query the secure security definer RPC proxy instead of exposing the raw table
        const { data, error } = await supabase.rpc('get_public_social_links');
        
        if (!error && Array.isArray(data) && data.length > 0) {
          setSocialLinks(data);
        }
      } catch (err) {
        console.warn('Footer - Could not load social links from config');
      }
    };
    fetchConfig();
  }, []);

  const handleSubscribe = async (e) => {
    e?.preventDefault();
    setMessage({ type: '', text: '' });

    if (!email?.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    try {
      await newsletterService?.subscribe(email, null, user?.id || null);
      setMessage({ 
        type: 'success', 
        text: 'Successfully subscribed! Check your email for confirmation.' 
      });
      setEmail('');
    } catch (err) {
      console.error('Subscribe error:', err);
      setMessage({ 
        type: 'error', 
        text: err?.message || 'Failed to subscribe. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const footerSections = [
    {
      title: 'Platform',
      links: [
        { name: 'Browse Models', path: '/model-catalog' },
        { name: '3D Viewer', path: '/3d-model-viewer' },
        { name: 'Shopping Cart', path: '/shopping-cart' },
        { name: 'My Account', path: '/user-account-dashboard' },
        { name: 'DataFabric', path: typeof window !== 'undefined' && window.location.origin.includes('localhost') ? 'http://localhost:3001' : 'https://datafabric.opengadget.com' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Sellers Toolbox', path: '/sellers-toolbox' },
        { name: 'Manufacturer Toolbox', path: '/manufacturer-toolbox' },
        { name: 'Suppliers Resource', path: '/suppliers-resource' },
        { name: 'Services Garage', path: '/services-garage' },
        { name: 'Documentation', path: '/documentation' },
        { name: 'API Reference', path: '/api-reference' },
        { name: 'Tutorials', path: '/tutorials' }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'Help Center', path: '/support-center' },
        { name: 'Contact Us', path: '/support-center' },
        { name: 'Bug Reports', path: '/support-center' },
        { name: 'Feature Requests', path: '/support-center' },
        { name: 'Community Forum', path: '/resources-hub' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', path: '/company-information' },
        { name: 'Careers', path: '/careers' },
        { name: 'Press Kit', path: '/company-information' },
        { name: 'Blog', path: '/blog' }
      ]
    }
  ];

  return (
    <footer className="bg-slate-900 text-white text-left">
      <div className="border-b border-slate-800">
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">Stay Updated with New Models</h3>
            <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
              Get notified about new 3D models, exclusive releases, and community highlights.
            </p>
            
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  disabled={loading}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                />
              </div>
              <Button type="submit" variant="default" iconName="Mail" iconPosition="left" disabled={loading}>
                {loading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
            {message?.text && (
              <div className={`mt-4 p-3 rounded-lg ${message?.type === 'success' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                {message?.text}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-6 gap-12">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-6 group">
                <img 
                  src="/assets/images/OpenGadget-SMALL-GREY-TRANS-1763756659192.png" 
                  alt="OpenGadget Logo"
                  className="h-12 w-auto object-contain group-hover:scale-105 transition-transform"
                />
            </Link>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Transforming product visualization through immersive 3D experiences. 
              Where precision meets imagination in the world of 3D modeling.
            </p>
            
            <div className="flex space-x-4">
              {socialLinks?.filter(s => s.isActive !== false).map((social, idx) => (
                <a
                  key={`${social?.name}-${idx}`}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center transition-all group"
                  aria-label={social?.name}
                >
                  <Icon 
                    name={social?.icon} 
                    size={18} 
                    className="group-hover:text-blue-400 transition-colors" 
                  />
                </a>
              ))}
            </div>
          </div>

          {footerSections?.map((section) => (
            <div key={section?.title}>
              <h4 className="font-bold text-white mb-4 uppercase text-xs tracking-widest">{section?.title}</h4>
              <ul className="space-y-3">
                {section?.links?.map((link) => (
                  <li key={link?.name}>
                    {link?.path.startsWith('http') ? (
                      <a 
                        href={link?.path} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-slate-400 hover:text-white transition-colors text-sm"
                      >
                        {link?.name}
                      </a>
                    ) : (
                      <Link to={link?.path} className="text-slate-400 hover:text-white transition-colors text-sm">
                        {link?.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col lg:flex-row justify-between items-center gap-6">
            <div className="text-slate-400 text-sm italic">
              © {currentYear} OpenGadget. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-xs font-bold uppercase tracking-wider">
              <Link to="/privacy-policy" className="text-slate-500 hover:text-white">Privacy</Link>
              <Link to="/terms-of-service" className="text-slate-500 hover:text-white">Terms</Link>
              <Link to="/cookie-policy" className="text-slate-500 hover:text-white">Cookies</Link>
            </div>
            <div className="flex items-center space-x-4 opacity-50">
              <Icon name="Shield" size={14} />
              <Icon name="Award" size={14} />
            </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
